import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, Download, RotateCcw, Upload, Loader2, Plus, CheckCircle2 } from "lucide-react";
import api from "@/lib/api";
import { formatApiErrorDetail, fmtDateTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Summary } from "@/components/company/ArchiveCard";

const ONE = { clienti: "cliente", preventivi: "preventivo", fatture: "fattura", appuntamenti: "appuntamento", spese: "spesa", cantieri: "cantiere", pagamenti: "pagamento" };
const count = ([k, n]) => `${n} ${n === 1 ? ONE[k] || k : k}`;
const kb = (n) => (n > 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

const Row = ({ b, busy, onDownload, onRestore }) => (
  <div className="flex flex-wrap items-center gap-3 rounded-lg border border-border px-3 py-2.5" data-testid={`backup-row-${b.id}`}>
    <div className="flex-1 min-w-[180px]">
      <div className="text-sm font-medium">{fmtDateTime(b.created_at)} <span className="text-xs font-normal text-muted-foreground">· {b.kind === "settimanale" ? "automatica" : "manuale"} · {kb(b.size)}</span></div>
      <div className="text-xs text-muted-foreground">{Object.entries(b.counts || {}).map(count).join(" · ") || "nessun dato"}</div>
    </div>
    <Button size="sm" variant="outline" onClick={() => onDownload(b)} disabled={!!busy} data-testid={`backup-download-${b.id}`}><Download className="w-3.5 h-3.5 mr-1" /> Scarica</Button>
    <Button size="sm" variant="outline" onClick={() => onRestore(b)} disabled={!!busy} data-testid={`backup-restore-${b.id}`}><RotateCcw className="w-3.5 h-3.5 mr-1" /> Ripristina</Button>
  </div>
);

// Copie di sicurezza della ditta: automatiche ogni lunedì, manuali, scaricabili e ripristinabili.
export const BackupCard = () => {
  const ref = useRef(null);
  const input = useRef(null);
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState("");
  const [rep, setRep] = useState(null);
  const [pending, setPending] = useState(null);

  const load = () => api.get("/company/backups").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); if (window.location.hash === "#copie") setTimeout(() => ref.current?.scrollIntoView({ behavior: "smooth" }), 400); }, []);

  const run = async (key, fn) => {
    setBusy(key);
    try { await fn(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setBusy("");
  };
  const create = () => run("create", async () => { await api.post("/company/backups"); await load(); toast.success("Copia di sicurezza creata"); });
  const download = (b) => run(`dl-${b.id}`, async () => {
    const { data, headers } = await api.get(`/company/backups/${b.id}/download`, { responseType: "blob" });
    const name = /filename="([^"]+)"/.exec(headers["content-disposition"] || "")?.[1] || "kgestionale-copia.json.gz";
    const a = Object.assign(document.createElement("a"), { href: URL.createObjectURL(data), download: name });
    a.click();
    URL.revokeObjectURL(a.href);
  });
  const restore = (src, dry) => {
    if (!src.file) return api.post(`/company/backups/${src.id}/restore?dry_run=${dry}`);
    const fd = new FormData();
    fd.append("file", src.file);
    return api.post(`/company/backups/restore-file?dry_run=${dry}`, fd);
  };
  const preview = (src) => { setRep(null); setPending(null); run("check", async () => { const { data } = await restore(src, true); setPending(src); setRep(data); }); };
  const confirm = () => run("restore", async () => {
    const { data } = await restore(pending, false);
    setRep(data); setPending(null); toast.success("Copia ripristinata");
  });
  const pick = (e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) preview({ file: f }); };
  const hasChanges = rep && Object.values(rep.collezioni).some((s) => s.nuovi || s.aggiornati);

  return (
    <div ref={ref} id="copie" className="rounded-xl border border-border bg-card p-5 sm:p-6 space-y-4 scroll-mt-20" data-testid="backup-card">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-emerald-500/10 text-emerald-600 flex items-center justify-center shrink-0"><ShieldCheck className="w-5 h-5" /></div>
        <div className="flex-1">
          <h3 className="font-heading font-semibold">Copie di sicurezza</h3>
          <p className="text-xs text-muted-foreground">Ogni lunedì alle 7:00 salvo una copia di tutti i dati della ditta e ti avviso via email. Le password non sono incluse. Il ripristino aggiunge i dati mancanti e non cancella niente.</p>
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button onClick={create} disabled={!!busy} data-testid="backup-create-button">{busy === "create" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4 mr-1.5" />} Crea una copia adesso</Button>
        <Button variant="outline" onClick={() => input.current?.click()} disabled={!!busy} data-testid="backup-upload-button"><Upload className="w-4 h-4 mr-1.5" /> Ripristina da file</Button>
        <input ref={input} type="file" accept=".gz,.json,application/gzip,application/json" className="hidden" onChange={pick} data-testid="backup-file-input" />
      </div>
      <div className="space-y-2" data-testid="backup-list">
        {items === null ? <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
          : items.length ? items.map((b) => <Row key={b.id} b={b} busy={busy} onDownload={download} onRestore={(x) => preview({ id: x.id })} />)
          : <p className="text-sm text-muted-foreground" data-testid="backup-empty">Nessuna copia ancora: la prima arriva lunedì, oppure creala adesso.</p>}
      </div>
      {busy === "check" && <div className="text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Controllo la copia…</div>}
      {rep && <Summary rep={rep} />}
      {rep?.dry_run && pending && hasChanges && (
        <div className="flex gap-2">
          <Button onClick={confirm} disabled={!!busy} data-testid="backup-confirm-restore">{busy === "restore" ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1.5" />} Conferma ripristino</Button>
          <Button variant="ghost" onClick={() => { setRep(null); setPending(null); }} data-testid="backup-cancel-restore">Annulla</Button>
        </div>
      )}
    </div>
  );
};

export default BackupCard;
